/**
 * Retry utility for Supabase queries with exponential backoff.
 * Handles transient database timeouts (PGRST, 5xx, network errors).
 */

interface RetryOptions {
  maxRetries?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  onRetry?: (attempt: number, error: unknown) => void;
}

const DEFAULT_OPTIONS: Required<Omit<RetryOptions, 'onRetry'>> = {
  maxRetries: 3,
  baseDelayMs: 500,
  maxDelayMs: 5000,
};

const isRetryableError = (error: any): boolean => {
  if (!error) return false;
  const message = error?.message || error?.code || '';
  const retryablePatterns = [
    'PGRST',           // PostgREST errors (schema cache, etc.)
    'timeout',
    'connection',
    'ECONNRESET',
    'ECONNREFUSED',
    'fetch failed',
    'network',
    'SSL handshake',
    '502', '503', '504', '520', '525',
    'statement timeout',
    'too many connections',
    'Could not query the database',
  ];
  return retryablePatterns.some(p => 
    String(message).toLowerCase().includes(p.toLowerCase())
  );
};

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Wraps a Supabase query function with retry logic.
 * 
 * Usage:
 * ```ts
 * const { data, error } = await withRetry(() =>
 *   supabase.from('table').select('*').eq('id', someId)
 * );
 * ```
 */
export async function withRetry<T extends { error: any }>(
  queryFn: () => PromiseLike<T>,
  options?: RetryOptions
): Promise<T> {
  const { maxRetries, baseDelayMs, maxDelayMs } = { ...DEFAULT_OPTIONS, ...options };

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const result = await queryFn();

    // Success or non-retryable error
    if (!result.error || !isRetryableError(result.error) || attempt === maxRetries) {
      return result;
    }

    // Calculate delay with exponential backoff + jitter
    const delay = Math.min(
      baseDelayMs * Math.pow(2, attempt) + Math.random() * baseDelayMs,
      maxDelayMs
    );

    console.warn(
      `[supabaseRetry] Attempt ${attempt + 1}/${maxRetries} failed (${result.error?.message || result.error?.code}). Retrying in ${Math.round(delay)}ms...`
    );

    options?.onRetry?.(attempt + 1, result.error);
    await sleep(delay);
  }

  // TypeScript: unreachable, but satisfies compiler
  return queryFn();
}

/**
 * Wraps an async function (non-Supabase shape) with retry logic.
 * For use with supabase.auth.*, supabase.functions.invoke(), etc.
 */
export async function withRetryAsync<T>(
  fn: () => Promise<T>,
  options?: RetryOptions
): Promise<T> {
  const { maxRetries, baseDelayMs, maxDelayMs } = { ...DEFAULT_OPTIONS, ...options };

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (!isRetryableError(error) || attempt === maxRetries) {
        throw error;
      }

      const delay = Math.min(
        baseDelayMs * Math.pow(2, attempt) + Math.random() * baseDelayMs,
        maxDelayMs
      );

      console.warn(
        `[supabaseRetry] Attempt ${attempt + 1}/${maxRetries} failed. Retrying in ${Math.round(delay)}ms...`
      );

      options?.onRetry?.(attempt + 1, error);
      await sleep(delay);
    }
  }

  // TypeScript: unreachable
  return fn();
}
